import React, { useEffect, useState } from 'react';
import { Form, Switch, Typography, Input, Row, Col } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from '../../Components/Modals/CustomTooltip';
import { isDomainClean } from '../../../services/utils/validationUtils';
import Tutorial from './Tutorial';

const { Title, Text } = Typography;
const InfoIcon = InfoCircleOutlined;

const PermissionsStep = ({
    form,
    formData,
    setFormData,
    handleFormChange,
    isValidAllowedDomain,
    // Nuovi props per il campo subdomain
    isDomainInputDisabled,
    isValidDomainFormat,
    isDomainAvailable,
    setDomainValidating,
    domainValidating,
    setFormattedDomainUrl,
    // Nuovi props per gestire le licenze
    availableLicenses = [],
    selectedLicenseId
}) => {

    // Determina il tipo di licenza selezionata e il target domain
    const getSelectedLicenseInfo = () => {
        if (!selectedLicenseId || !availableLicenses.length) {
            return { type: 'unknown', webUrl: null };
        }

        const selectedLicense = availableLicenses.find(license => license.id === selectedLicenseId);

        if (!selectedLicense) {
            return { type: 'unknown', webUrl: null };
        }

        const subscriptionType = selectedLicense.subscriptionType?.toLowerCase() || '';

        // Determina il tipo con la stessa logica usata nell'ordinamento
        let type = 'unknown';
        if (subscriptionType.includes('additional')) {
            type = 'additional';
        } else if (subscriptionType.includes('branded')) {
            type = 'branded';
        } else if (subscriptionType.includes('smart')) {
            type = 'smart';
        }

        return {
            type,
            webUrl: selectedLicense.webUrl || null,
            licenseName: selectedLicense.name || 'Unknown License'
        };
    };

    const licenseInfo = getSelectedLicenseInfo();
    const isBrandedOrSmart = licenseInfo.type === 'branded' || licenseInfo.type === 'smart';
    const isAdditional = licenseInfo.type === 'additional';

    // Effetto per resettare i campi quando cambia il tipo di licenza
    const [previousLicenseId, setPreviousLicenseId] = useState(null);

    useEffect(() => {
        if (previousLicenseId && previousLicenseId !== selectedLicenseId && selectedLicenseId) {
            // Reset solo i campi specifici delle permissions, non tutto
            const currentPermissions = formData.permissions || {};
            const resetPermissions = {
                ...currentPermissions,
                requireLogin: false,
                loginPassword: '',
                // Non resettare iframeOnlyAccess qui, sarà gestito dall'altro useEffect
            };

            setFormData(prev => ({
                ...prev,
                permissions: resetPermissions,
                domain: '' // Reset anche il domain per additional
            }));

            // Reset anche i campi specifici nel form
            form.setFieldsValue({
                permissions: {
                    ...form.getFieldValue('permissions'),
                    requireLogin: false,
                    loginPassword: ''
                },
                domain: ''
            });
        }

        // Aggiorna l'ID della licenza precedente
        setPreviousLicenseId(selectedLicenseId);
    }, [selectedLicenseId, setFormData, form, previousLicenseId, formData.permissions]);

    // Effetto per gestire iframe only access basato sul tipo di licenza
    useEffect(() => {
        if (isBrandedOrSmart && formData.permissions?.iframeOnlyAccess !== true) {
            // Assicuriamoci che l'oggetto permissions esista
            const currentPermissions = formData.permissions || {};
            const newPermissions = {
                ...currentPermissions,
                iframeOnlyAccess: true
            };

            // Aggiorna solo se il valore non è già impostato
            setFormData(prev => ({
                ...prev,
                permissions: newPermissions
            }));

            // Aggiorna anche il form
            form.setFieldsValue({ permissions: newPermissions });
        } else if (isAdditional && formData.permissions?.iframeOnlyAccess === true) {
            // Per additional, rimuoviamo iframe only access se è attualmente attivo
            const currentPermissions = formData.permissions || {};
            const newPermissions = {
                ...currentPermissions,
                iframeOnlyAccess: false
            };

            setFormData(prev => ({
                ...prev,
                permissions: newPermissions
            }));

            // Aggiorna anche il form
            form.setFieldsValue({ permissions: newPermissions });
        }
    }, [isBrandedOrSmart, isAdditional, licenseInfo.type, formData.permissions?.iframeOnlyAccess, setFormData, form]);

    // Form fields are now synchronized via useEffect in the parent stepper component

    // Gestisci il cambio di Require Login
    const handleRequireLoginChange = (value) => {
        const newPermissions = {
            ...formData.permissions,
            requireLogin: value,
            // Se Required Login diventa false, resetta la password
            loginPassword: value ? formData.permissions.loginPassword : ''
        };

        // Aggiorna lo stato
        setFormData(prev => ({
            ...prev,
            permissions: newPermissions
        }));

        // Chiama handleFormChange per mantenere la compatibilità
        handleFormChange({ permissions: newPermissions });
    };

    // Gestisci gli altri campi normalmente
    const handleOtherFieldsChange = (changedValues) => {
        // Solo per campi che non sono public o requireLogin
        if (!changedValues.permissions?.hasOwnProperty('public') &&
            !changedValues.permissions?.hasOwnProperty('requireLogin')) {
            handleFormChange(changedValues);
        }
    };

    return (
        <Row gutter={32} style={{ minHeight: 400 }}>
            {/* Left: Form */}
            <Col xs={24} md={12}>
                <div style={{ padding: '1.25rem 1rem' }}>
                    <Title level={4}>Catalogue Permissions</Title>
                    <Text type="secondary">
                        Configure who can access your catalogue and set the subdomain.
                    </Text>

                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: '1.5rem' }}
                        onValuesChange={handleOtherFieldsChange}
                    >
                        {/* Mostra target domain per branded/smart */}
                        {isBrandedOrSmart && (
                            <Form.Item
                                label="Target Domain"
                            >
                                <Input
                                    value={licenseInfo.webUrl || '-'}
                                    disabled
                                    style={{ backgroundColor: '#f5f5f5' }}
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    This is the target domain from your {licenseInfo.licenseName} license
                                </Text>
                            </Form.Item>
                        )}

                        {/* Mostra input subdomain per additional */}
                        {isAdditional && (
                            <Form.Item
                                name="domain"
                                label="Subdomain"
                                validateStatus={domainValidating ? 'validating' : null}
                                hasFeedback
                                rules={[
                                    { required: true, message: 'Please enter a subdomain' },
                                    {
                                        validator: async (_, value) => {
                                            // Assicuriamoci che value sia una stringa
                                            const safeValue = typeof value === 'string' ? value : '';

                                            if (!safeValue) return Promise.resolve();

                                            // Verifica che il formato sia valido (solo lettere minuscole, numeri e trattini)
                                            if (!isValidDomainFormat(safeValue)) {
                                                return Promise.reject(new Error('Subdomain can only contain lowercase letters, numbers, and hyphens'));
                                            }

                                            // Verifica che non contenga parole inappropriate
                                            if (!isDomainClean(safeValue)) {
                                                return Promise.reject(new Error('Subdomain contains inappropriate words'));
                                            }

                                            setDomainValidating(true);
                                            try {
                                                await new Promise(resolve => setTimeout(resolve, 300));
                                                const isAvailable = await isDomainAvailable(safeValue);
                                                setDomainValidating(false);

                                                if (!isAvailable) {
                                                    return Promise.reject(new Error('This domain is already in use'));
                                                }

                                                // Formatta l'URL completo per la visualizzazione
                                                const formattedUrl = `https://${safeValue}.webvto.it`;
                                                if (setFormattedDomainUrl) {
                                                    setFormattedDomainUrl(formattedUrl);
                                                }
                                                return Promise.resolve();
                                            } catch (error) {
                                                setDomainValidating(false);
                                                return Promise.reject(new Error('Failed to check domain availability'));
                                            }
                                        },
                                        validateTrigger: ['onChange', 'onBlur']
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="Enter subdomain"
                                    addonBefore="https://"
                                    addonAfter=".webvto.it"
                                    onChange={(e) => {
                                        if (setFormattedDomainUrl) {
                                            setFormattedDomainUrl('');
                                        }
                                        const value = e.target.value;
                                        // Assicuriamoci di salvare solo la parte del sottodominio
                                        if (value) {
                                            // Rimuoviamo eventuali caratteri non validi per un sottodominio
                                            const cleanValue = value.replace(/[^a-z0-9-]/g, '');
                                            if (cleanValue !== value) {
                                                e.target.value = cleanValue;
                                            }
                                            form.validateFields(['domain']);
                                        }
                                    }}
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            name={['permissions', 'requireLogin']}
                            label="Require Login"
                            valuePropName="checked"
                        >
                            <Switch
                                checked={formData.permissions?.requireLogin}
                                onChange={handleRequireLoginChange}
                            />
                        </Form.Item>

                        {formData.permissions?.requireLogin && (
                            <Form.Item
                                name={['permissions', 'loginPassword']}
                                label="Password"
                                rules={[
                                    { required: true, message: 'Please enter a password' },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            if (!isDomainClean(value)) {
                                                return Promise.reject(new Error('Password contains inappropriate words'));
                                            }
                                            return Promise.resolve();
                                        },
                                        validateTrigger: ['onChange', 'onBlur']
                                    }
                                ]}
                            >
                                <Input.Password placeholder="Enter password" />
                            </Form.Item>
                        )}

                        {/* Mostra Iframe Only Access solo per branded/smart */}
                        {isBrandedOrSmart && (
                            <>
                                <Form.Item
                                    name={['permissions', 'iframeOnlyAccess']}
                                    label={<>
                                        Iframe Only Access
                                        <CustomTooltip title="This is automatically enabled for Branded and Smart licenses">
                                            <InfoIcon style={{ fontSize: 16, marginLeft: 8, color: '#999' }} />
                                        </CustomTooltip>
                                    </>}
                                    valuePropName="checked"
                                >
                                    <Switch
                                        disabled={true}
                                        checked={formData.permissions?.iframeOnlyAccess}
                                    />
                                </Form.Item>

                                <Text type="secondary" style={{ fontSize: '12px', marginTop: '-10px', display: 'block' }}>
                                    Iframe Only Access is automatically enabled for {licenseInfo.type} licenses
                                </Text>
                            </>
                        )}
                    </Form>
                </div>
            </Col>
            {/* Right: Tutorial */}
            <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                <Tutorial
                    title="Catalogue Permissions"
                    description={
                        <>
                            {isAdditional ? (
                                <>
                                    Configure your catalogue subdomain and access permissions.<br />
                                    For Additional licences, you can set a custom subdomain on webvto.it and optionally require a password for access.<br /><br />
                                    <b>Subdomain Guidelines:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Use only lowercase letters, numbers, and hyphens</li>
                                        <li>Choose a memorable name that represents your brand</li>
                                        <li>Your catalogue will be accessible at https://[subdomain].webvto.it</li>
                                    </ul>
                                    <br />
                                    <b>Access Control:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Enable "Require Login" to protect your catalogue with a password</li>
                                        <li>Users will need to enter the password before viewing your catalogue</li>
                                    </ul>
                                </>
                            ) : isBrandedOrSmart ? (
                                <>
                                    Configure access permissions for your {licenseInfo.type} catalogue.<br />
                                    The target domain is automatically set from your licence, and iframe-only access is enabled for security. You can optionally require a password for additional protection.<br /><br />
                                    <b>Domain Settings:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Target domain is inherited from your {licenseInfo.type} licence</li>
                                        <li>Iframe-only access is automatically enabled for security</li>
                                        <li>Your catalogue will be embedded on the specified domain</li>
                                    </ul>
                                    <br />
                                    <b>Access Control:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Enable "Require Login" for additional password protection</li>
                                        <li>Useful for restricted access within your embedded catalogue</li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    Configure access permissions for your catalogue.<br />
                                    Control who can view your catalogue by requiring a password for access.<br /><br />
                                    <b>Access Control:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Enable "Require Login" to protect your catalogue with a password</li>
                                        <li>Users will need to enter the password before viewing your catalogue</li>
                                        <li>Leave disabled for public access to your catalogue</li>
                                    </ul>
                                </>
                            )}
                        </>
                    }
                />
            </Col>
        </Row>
    );
};

export default PermissionsStep;